import React, { useMemo } from 'react';
import CardBox from '@/pages/dashboard/components/CardBox';
import './index.scss';
import moment from 'moment';
import { shallowEqual, useIntl, useSelector } from 'umi';
import HorizontalBarChart from '@/pages/dashboard/pages/forecast/components/UnitSalesBarChart/component/HorizontalBarChart';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { getName } from '@home/utils/common';
import { dynamicString } from '@home/utils/dynamicString';
import { commonDownloadValueFormatter, formateCategoryName, handleDownload } from '@/pages/dashboard/utils/utils';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';

const UnitSalesBarChart = (props: any) => {
  const {
    className,
    updatedTime,
    data: propsData,
    dataKey,
    height,
    titleEn,
    titleCn,
    downloadFileTitleEn,
    downloadFileTitleCn,
    tableColumnsMap,
    maxCategoryWidth = 80,
    tooltipConfig,
    xLabelFormat,
    formatRule = "intBmk00",
    downloadFormatRule = "int",
    tableHeight=320,
  } = props;
  const { data, loading, errorMsg: error } = propsData?.[dataKey] || {};
  const { releaseTime, gameCode } = useSelector((state: any) => ({
    releaseTime: state?.dashboardSetting?.gameConfig?.game_phase_timeline?.release_time,
    gameCode: state.dashboard?.currentGame?.game_code
  }), shallowEqual);
  const { formatMessage } = useIntl();
  const { isEN } = useLocal();
  const diffReleaseDate = moment(updatedTime).diff(moment(releaseTime), 'days');
  const title = getName(isEN,titleEn, titleCn);
  const downLoadTitle = getName(isEN,downloadFileTitleEn, downloadFileTitleCn);
  const { tableData, series, yData, tableColumns, key2, currentGameNames, maxItem, downloadData } = useMemo(() => {
    // 不同情况key值不一样
    let key2;
    let key3;
    const values = data?.[0]?.values || {};
    if (Object.hasOwn(values, 'current_m1')) {
      key2 = 'current_m1';
      key3 = 'm1_y1';
    } else if (Object.hasOwn(values, 'current_q1')) {
      key2 = 'current_q1';
      key3 = 'q1_y1';
    } else if (Object.hasOwn(values, 'current_h1')) {
      key2 = 'current_h1';
      key3 = 'h1_y1';
    } else if (Object.hasOwn(values, 'current_y1')) {
      key2 = 'current_y1';
    }

    // 根据current从小到大排序,数值相同，根据名称排序
    const newData = data?.sort((a, b) => {
      const valueA = Number(a?.values?.current || 0);
      const valueB = Number(b?.values?.current || 0);
      if (valueA !== valueB) {
        return valueA - valueB;
      }
      return a?.game_name?.localeCompare?.(b.game_name);
    });

    // 获取总和最大值
    let maxItem;
    let maxValue = 0;
    data?.forEach((item) => {
      let totalVal = 0;
      Object.keys(item?.values || {})?.forEach((key) => {
        const value = Number(item?.values?.[key] || 0);
        totalVal += value;
      });
      if (totalVal > maxValue) {
        maxValue = totalVal;
        maxItem = item?.values || {};
      }
    });

    const series: any[] = [];
    // 转换table的column配置
    const columns =
      Object.keys(tableColumnsMap || {}).map((key) => {
        const {
          nameEn = '',
          nameCn = '',
        } = tableColumnsMap?.[key] || {};
        return {
          header: getName ( isEN, dynamicString(nameEn, { diffDay: diffReleaseDate + 1 }),dynamicString(nameCn, { diffDay: diffReleaseDate + 1 })),
          key,
        };
      }) || [];

    // 固定游戏、current列
    const gameColumn = columns.find((item) => item.key === 'game_name') || {};
    const currentColumn = columns.find((item) => item.key === 'current') || {};
    const rawColumns: any[] = [gameColumn, currentColumn];

    if (key2) {
      const name2 = getName(
        isEN,
        tableColumnsMap?.[key2]?.nameEn,
        tableColumnsMap?.[key2]?.nameCn,
      );
      series.push({
        name: name2,
        data: newData?.map((item) => item?.values?.[key2]),
      });
      const column3 = columns.find((item) => item.key === key2) || {};
      rawColumns.push(column3);
    }

    if (key3) {
      const name3 = getName(
        isEN,
        tableColumnsMap?.[key3]?.nameEn,
        tableColumnsMap?.[key3]?.nameCn,
      );
      series.push({
        name: name3,
        data: newData?.map((item) => item?.values?.[key3]),
      });
      const column4 = columns.find((item) => item.key === key3) || {};
      rawColumns.push(column4);
    }

    // 在返回之前给 game_name 加上 render
    const tableColumns = rawColumns.map(col => {
      if (col.key === 'game_name') {
        return {
          ...col,
          render: (record) => {
            const value = record?.game_name;
            return currentGameNames?.includes(value)
              ? <div style={{ color: '#FFBD57', fontWeight: 'bold' }}>{value}</div>
              : value;
          }
        };
      }
      return col;
    });

    // custom的必须放到最后一个，不然会影响柱子的初始位置
    const name1 = dynamicString(
      getName(
        isEN,
        tableColumnsMap?.current?.nameEn,
        tableColumnsMap?.current?.nameCn,
      ),
      { diffDay: diffReleaseDate + 1 },
    );
    series.push({
      type: key2 ? 'custom' : 'bar',
      name: name1,
      data: newData?.map((item) => item?.values?.current),
      renderItem: (params, api) => {
        // 计算current竖线的位置、宽度、高度
        const categoryIndex = api.value(0);
        const yValue = api.coord([categoryIndex, params.dataIndex]);
        const barHeight = api.size([0, 1])[1] * 0.6;
        return {
          type: 'line',
          shape: {
            x1: yValue[0],
            y1: yValue[1] - barHeight / 2,
            x2: yValue[0],
            y2: yValue[1] + barHeight / 2,
          },
          style: {
            stroke: '#69D19C',
            lineWidth: 3,
          },
        };
      },
      z: 10,
    });

    const yData = newData?.map((item) => item.game_name);

    // 根据game_code匹配对应的名称
    const currentGameNames = newData ?.filter((item) => item.game_code === gameCode)?.map((item) => item.game_name) || [];
    
    const { tableData, downloadData } = (() => {
      const reversedData = newData?.reverse() || [];
    
      const tableData: any[] = [];
      const downloadData: any[] = [];
    
      reversedData.forEach((item: {values: string;}) => {
        const formattedTableValues = Object.fromEntries(
          Object.entries(item.values || {}).map(([key, value]) => [
            key,
            dataFormat({ value, formatRule }),
          ])
        );
    
        const formattedDownloadValues = Object.fromEntries(
          Object.entries(item.values || {}).map(([key, value]) => [
            key,
            commonDownloadValueFormatter(downloadFormatRule, value),
          ])
        );
    
        const { values, ...rest } = item;
    
        tableData.push({
          ...rest,
          ...formattedTableValues,
        });
    
        downloadData.push({
          ...rest,
          ...formattedDownloadValues,
        });
      });
    
      return { tableData, downloadData };
    })();
    

    return {
      tableColumns,
      downloadData,
      tableData,
      series,
      yData,
      key2,
      currentGameNames,
      maxItem,
    };
  }, [data, diffReleaseDate, formatRule, gameCode, isEN, tableColumnsMap]);

  const setOtherOptions = (options: any) => {
    const newOptions = { ...options };
    // 只有current的时候不需要计算
    if (key2) {
      // 计算除current以外的总和最大值
      newOptions.xAxis.max = (value) => value.max - (maxItem?.current || 0);
    }

    newOptions.yAxis[0].axisLabel.formatter = (gameName: string) => {
      // 针对接口demo/dune名称跟经分名称不一致
      const isCurrentGame = currentGameNames?.includes(gameName);
      // Y轴分类换行处理
      const newGameName = formateCategoryName(
        gameName,
        maxCategoryWidth,
        isCurrentGame ? 6.5 : 6,
      );
      return isCurrentGame ? `{bold|${newGameName}}` : newGameName;
    };

    // 把current放到第一位
    const nameList = newOptions.series?.map((item) => item?.name);
    if (nameList.length > 1) {
      const lastName = nameList.pop();
      nameList.unshift({
        name: lastName,
        icon: 'path://M0,0 L2,0 L2,8 L0,8 Z',
      });
    }
    newOptions.legend.data = nameList;
    newOptions.legend.selectedMode = false;
    if (key2 === 'current_y1') {
      newOptions.color = [
        '#5086F3',
        '#69D19C',
        '#6E7794',
        '#F8D74D',
        '#7F6FF1',
        '#5DC8F1',
      ];
    } else if (!key2) {
      newOptions.color = [
        '#69D19C',
        '#5086F3',
        '#6E7794',
        '#F8D74D',
        '#7F6FF1',
        '#5DC8F1',
      ];
    } else {
      newOptions.color = [
        '#5086F3',
        '#6E7794',
        '#69D19C',
        '#F8D74D',
        '#7F6FF1',
        '#5DC8F1',
      ];
    }

    return newOptions;
  };

  // 把current放到第一位
  const tooltipParamsFormat = (params: any[]) => {
    const newParams =
        params?.map((item) => {
          if (item?.seriesType === 'custom') {
            return { ...item, iconType: 'vertical-line' };
          }
          return item;
        }) || [];
    if (newParams.length > 1) {
      const lastName = newParams.pop();
      newParams.unshift(lastName);
    }
    return newParams;
  };

  // 重新计算X轴的max值
  const getChart = (chart: any) => {
    // 只有current的时候不需要计算
    if (key2) {
      const options: any = chart.getOption();
      // 获取X轴原最大数值
      const scope = chart.getModel().getComponent('xAxis').axis.scale._extent;
      // 获取X轴区间数值
      const interval = chart.getModel().getComponent('xAxis').axis.scale._interval;
      if (options?.xAxis?.[0]) {
        const surplusValue = scope?.[1] % interval;
        options.xAxis[0].max = scope?.[1] - surplusValue + interval;
        options.animation = false;
        chart?.setOption(options);
      }
    }
  };

  const renderList = useMemo(() => [
    {
      type: 'barChart',
      svgType: 'column-chart',
      content: formatMessage({ id: 'common_bar_chart' }),
      component: <HorizontalBarChart
        series={series}
        yData={yData}
        setOtherConfigFunc={setOtherOptions}
        maxCategoryWidth={maxCategoryWidth}
        getChart={getChart}
        tooltipParamsFormat={tooltipParamsFormat}
        tooltipConfig={tooltipConfig}
        isEn={isEN}
        xLabelFormat={xLabelFormat}

      />
    },
    {
      type: 'sheet',
      svgType: 'sheet',
      content: formatMessage({ id: "common_table" }),
      component: <MiniTable
        loading={loading}
        tableOption={tableColumns || []}
        tableList={tableData || []}
        height={tableHeight} 
      />,
    },
  ], [series, yData, maxCategoryWidth, tooltipConfig, isEN, xLabelFormat, loading, tableColumns, tableData, tableHeight]);

  return (
    <div className='dbd-unit-sales-bar-chart'>
      {
        <CardBox
          className={className}
          titleCom={title}
          height={height||''}
          renderList={renderList}
          downloadClick={() => handleDownload({data: tableData, option: tableColumns, downloadData}, (downLoadTitle || title), false)}
          isLoading={loading}
          isError={error}
          isNodata={!data}
        />
      }
    </div>
  );
};

export default UnitSalesBarChart;

